<template>
  <Modal>
    <div slot="header">
      <div class="modal-header__content">
        <div class="modal-header__player">
          <h3 class="text-bold text-lg mb-12 text-white">Assign player</h3>
          <div class="assignment-modal__team text-semi">
            <div class="swatch" :style="{ backgroundColor: color }"></div>
            <span class="mr-16" :style="{ color: color }">
              Team {{ team }}
            </span>
            <span class="text-grey">/ {{ position.name }}</span>
          </div>
        </div>
        <button class="modal-header__close" @click="toggle">
          <svg-icon icon="x"></svg-icon>
        </button>
      </div>
    </div>
    <div slot="body">
      <div class="assignment-modal__search">
        <input
          type="search"
          class="assignment-modal__search-input"
          v-model="searchable"
          placeholder="Type to search"
          ref="search" />
        <svg-icon icon="search"></svg-icon>
      </div>
      <simplebar class="assignment-modal__list">
        <div
          class="assignment-modal__player"
          v-for="player in filteredItems"
          :key="player.player.id"
          @click="selectPlayer(player)">
          <profile-pic
            v-show="false"
            :src="player.player.photo_url"
            :size="40"
            circle></profile-pic>
          <span class="mr-16 text-semi">{{ player.player.name }}</span>
          <div class="ml-auto assignment-modal__positions">
            <span v-if="eventId" :class="gameCountClass(player)" class="mr-8">
              {{
                getPlayerGameCount(player) > 0
                  ? getPlayerGameCount(player)
                  : '!'
              }}
            </span>
            <rating
              v-if="
                player.player_id &&
                player.player &&
                player.player.average_rating &&
                player.player.average_rating[0] &&
                player.player.average_rating[0].rating
              "
              :rating="player.player.average_rating[0].rating" />
            <tag-item
              v-for="position in player.player.preffered_positions"
              :key="position.id"
              >{{ position.short_name }}</tag-item
            >
          </div>
        </div>
      </simplebar>
    </div>
  </Modal>
</template>

<script>
  import Modal from '@/components/Modal';
  import { mapState } from 'vuex';

  export default {
    name: 'AssignPlayerModal',
    components: { Modal },
    props: {
      toggle: {
        type: Function,
        required: true,
      },
      players: {
        required: false,
      },
      position: {
        required: false,
      },
      team: {
        required: false,
      },
      color: {
        type: String,
        required: false,
      },
      searchOptions: {
        type: [Object, Boolean],
        default: false,
      },
      eventId: {
        type: Number,
        required: false,
      },
      game: {
        type: Object,
        required: false,
      },
    },
    data: function () {
      return {
        searchable: '',
        filteredItems: [],
      };
    },
    mounted() {
      this.filteredItems = this.sortedPlayers;
    },
    methods: {
      selectPlayer(payload) {
        this.$emit('selectPlayer', payload);
        this.toggle();
      },
      getPlayerRating(player) {
        return player.player &&
          player.player.average_rating &&
          player.player.average_rating[0]
          ? player.player.average_rating[0].rating
          : null;
      },
      getPlayerGameCount(player) {
        if (!this.eventId || !player.player || !player.player.games) return 0;
        return player.player.games.filter((g) => g.event_id == this.eventId)
          .length;
      },
      gameCountClass(player) {
        const count = this.getPlayerGameCount(player);
        return count === 0
          ? 'assignment-modal__badge assignment-modal__badge--alert'
          : 'assignment-modal__badge';
      },
      hasPositionMatch(player) {
        const positions = player.player
          ? player.player.preffered_positions || []
          : [];
        return positions.some(
          (pos) => pos.short_name === this.position.short_name,
        );
      },
      ratingDistance(rating) {
        if (rating === null || !this.referenceRating) return Infinity;
        const { lower, higher } = this.referenceRating;
        if (rating >= lower && rating <= higher) return 0;
        return Math.min(Math.abs(rating - lower), Math.abs(rating - higher));
      },
    },
    computed: {
      ...mapState({
        lineups: (state) => state.games.lineups,
      }),
      referenceRating() {
        if (this.game && this.game.skill_tier) {
          const parts = this.game.skill_tier.split(' - ');
          const lower = parseFloat(parts[0]);
          const higher = parseFloat(parts[1]);
          if (!isNaN(lower) && !isNaN(higher)) {
            return { lower, higher, mid: (lower + higher) / 2 };
          }
        }
        if (this.lineups) {
          const ratings = [];
          this.lineups.forEach((team) => {
            (team.squad || []).forEach((slot) => {
              if (
                slot.player &&
                slot.player.average_rating &&
                slot.player.average_rating[0]
              ) {
                ratings.push(slot.player.average_rating[0].rating);
              }
            });
          });
          if (ratings.length > 0) {
            const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
            return { lower: avg, higher: avg, mid: avg };
          }
        }
        return null;
      },
      sortedPlayers() {
        if (!this.players) return [];
        return [...this.players].sort((a, b) => {
          const aMatch = this.hasPositionMatch(a);
          const bMatch = this.hasPositionMatch(b);
          const aRating = this.getPlayerRating(a);
          const bRating = this.getPlayerRating(b);
          const aDist = this.ratingDistance(aRating);
          const bDist = this.ratingDistance(bRating);
          const aInRange = aDist === 0 && aRating !== null;
          const bInRange = bDist === 0 && bRating !== null;

          // Tier: 0 = pos+range, 1 = pos only, 2 = range only, 3 = neither
          const aTier = aMatch ? (aInRange ? 0 : 1) : aInRange ? 2 : 3;
          const bTier = bMatch ? (bInRange ? 0 : 1) : bInRange ? 2 : 3;

          if (aTier !== bTier) return aTier - bTier;

          // Within same tier: rated before unrated
          if (aRating !== null && bRating === null) return -1;
          if (aRating === null && bRating !== null) return 1;

          // Both rated: sort by distance
          return aDist - bDist;
        });
      },
    },
    watch: {
      sortedPlayers: {
        immediate: true,
        handler(val) {
          if (!this.searchable || this.searchable.length <= 2) {
            this.filteredItems = val;
          }
        },
      },
      searchable(term) {
        if (term.length > 2) {
          this.$search(term, this.sortedPlayers, this.searchOptions).then(
            (results) => {
              this.filteredItems = results;
            },
          );
        } else {
          this.filteredItems = this.sortedPlayers;
        }
      },
    },
  };
</script>
